import { Map, Set } from 'immutable';

import { filterSectionsForChannels } from './listingsProfiles';
import { shapeVariationsForApp } from '../variations/shapeForApp';
import { setProfileErrors } from '../validations/profile';
import { isValidNumber } from '../number';
import { tagsFromArray } from '../tags';
import { sortShops } from '../product/shapeForApp';
import { shapeId } from '../listings/listings';
import { get } from '../iterable/get';

import { CHANNEL_BY_ID, ETSY } from '../../constants/channels';
import { PROFILE } from '../../constants/profiles';
import { AMOUNT } from '../../constants/product';
import { FORMS } from '../../constants/forms';
import { EMPTY } from '../../constants';

export function shapeVariationsProfileForApp({ channel, profile: source }) {
  const productsCount = get('productsCount')(source);
  const templateId = get('templateId')(source);
  const title = get('title')(source);
  const data = get('value')(source);
  const category = get('taxonomyId')(data);
  const profile = shapeVariationsForApp({ category, channel, data })
    .set('channel', channel)
    .set('id', String(templateId))
    .set('listingsCount', parseInt(productsCount) || 0)
    .set('title', title);

  switch (channel) {
    case ETSY: {
      return profile.set('category', category || AMOUNT.ZERO);
    }

    default: {
      return profile;
    }
  }
}

export function shapeTagsProfileForApp({ profile, channel }) {
  const {
    id,
    tags,
    invalid,
    profile_name: title,
    products_count: productsCount,
  } = profile;

  return Map({
    id,
    title,
    channel,
    invalid,
    listingsCount: parseInt(productsCount) || 0,
    tags: tagsFromArray(tags),
  });
}

export function shapeSaleProfileForApp({ profile, channel }) {
  const {
    id,
    name: title,
    price_value: price,
    price_type: priceType,
    products_count: productsCount,
    convert_to_cap: isConvertPriceToCap,
    title_value: titleValue,
    title_operation: titleOperation,
    description_value: descriptionValue,
    description_operation: descriptionOperation,
    start_date: startDate,
    end_date: endDate,
    status,
    tags = [],
    cents_value: cents,
    tags_operation: tagsOperation,
  } = profile;
  return Map({
    id,
    title,
    status,
    channel,
    startDate: new Date(startDate),
    endDate: new Date(endDate),
    price: parseInt(price),
    priceType,
    listingsCount: parseInt(productsCount) || 0,
    isConvertPriceToCap,
    titleValue,
    titleOperation,
    descriptionValue,
    descriptionOperation,
    tags: tagsFromArray(tags),
    newTags: EMPTY.ORDERED_MAP,
    cents: cents || '',
    tagsOperation,
  });
}

export function shapeListingsProfileForApp({ profile, userShops }) {
  const { id, connected_listings: listingsCount, name: title } = profile;

  function reduceTemplates(result, template) {
    const shopId = shapeId(template.shopId);
    const productId = shapeId(template.id);
    const db = template.userDb;

    if (!isValidNumber(productId) || (userShops && userShops.getIn(['byId', shopId, 'db']) !== db)) {
      return result;
    }

    const channel = CHANNEL_BY_ID[template.channel_id];
    result.channels = result.channels.add(channel);
    result.shopIds = result.shopIds.add(shopId);
    result.templates = result.templates.push(Map({ channel, db, productId, shopId }));
    return result;
  }

  function filterSectionsByExcluded(section) {
    return !profile.excluded_sections[section];
  }

  function sortTemplates(x, y) {
    return sortShops({ sorted: userShops.get('options') })(x.get('shopId'), y.get('shopId'));
  }

  const { channels, shopIds, templates } = profile.templates.reduce(
    reduceTemplates,
    { channels: EMPTY.SET, shopIds: EMPTY.SET, templates: EMPTY.LIST }
  );

  const sections = Set(
    FORMS
      .filter(filterSectionsForChannels({ channels }))
      .filter(filterSectionsByExcluded)
  );

  return Map({
    channels,
    id: shapeId(id),
    listingsCount: parseInt(listingsCount) || 0,
    sections,
    shopIds,
    title,
    templates: userShops
      ? templates.sort(sortTemplates)
      : templates,
  });
}

export function shapeProfileForApp({ type, profile, ...rest }) {
  switch (type) {
    case PROFILE.LISTINGS: {
      return shapeListingsProfileForApp({ profile, ...rest });
    }
    case PROFILE.SALES: {
      return shapeSaleProfileForApp({ profile, ...rest });
    }

    case PROFILE.TAGS: {
      return shapeTagsProfileForApp({ profile, ...rest });
    }

    case PROFILE.VARIATIONS: {
      return shapeVariationsProfileForApp({ profile, ...rest });
    }

    default: {
      return profile;
    }
  }
}

export function shapeListingsForApp({ response, type }) {
  function getThumbnailUrl(product) {
    switch (type) {
      case PROFILE.LISTINGS: {
        return product.thumbnail_url;
      }

      default: {
        return product.image;
      }
    }
  }

  function reduceProducts(result, product) {
    const { id, title } = product;

    return result.push(
      Map({
        productId: shapeId(id),
        title,
        thumbnailUrl: getThumbnailUrl(product),
      })
    );
  }

  try {
    switch (type) {
      case PROFILE.LISTINGS: {
        return response.data.reduce(reduceProducts, EMPTY.LIST);
      }

      default: {
        return response.connectedProducts[0].products.reduce(reduceProducts, EMPTY.LIST);
      }
    }
  } catch (error) {
    return undefined;
  }
}

export function shapeProfilesForApp({ channel, profiles, state: source, type, userShops }) {
  const state = source
    .set('byId', EMPTY.MAP)
    .set('ids', EMPTY.LIST)
    .set('listings', EMPTY.MAP)
    .set('titles', EMPTY.SET);

  function getId(profile) {
    switch (type) {
      case PROFILE.VARIATIONS: {
        return shapeId(profile.templateId);
      }

      default: {
        return shapeId(profile.id);
      }
    }
  }

  function getProfile(profile) {
    switch (type) {
      case PROFILE.LISTINGS: {
        return shapeProfileForApp({ profile, type, userShops });
      }

      default: {
        return setProfileErrors({
          profile: shapeProfileForApp({ channel, type, profile }),
          type,
        });
      }
    }
  }

  function reduceProfiles(reduction, item) {
    const profileId = getId(item);
    const profile = getProfile(item);

    function updateEditing(editing) {
      return editing.get(profileId) === null
        ? editing.set(profileId, profile)
        : editing;
    }

    function updateById(byId) {
      return byId.set(profileId, profile);
    }

    function updateIds(ids) {
      return ids.push(profileId);
    }

    function updateListings(listings) {
      return source.hasIn(['listings', profileId])
        ? listings.set(profileId, source.getIn(['listings', profileId]))
        : listings;
    }

    function updateTitles(titles) {
      return titles.add(profile.get('title'));
    }

    let result = reduction
      .update('byId', updateById)
      .update('ids', updateIds);

    if (result.has('editing')) {
      result = result.update('editing', updateEditing);
    }

    if (result.has('listings')) {
      result = result.update('listings', updateListings);
    }

    if (result.has('titles')) {
      result = result.update('titles', updateTitles);
    }

    return result;
  }

  switch (type) {
    case PROFILE.LISTINGS: {
      return profiles.data.reduce(reduceProfiles, state);
    }

    case PROFILE.VARIATIONS: {
      return (profiles.profiles || profiles.templates).reduce(reduceProfiles, state);
    }

    default: {
      return profiles.profiles.reduce(reduceProfiles, state);
    }
  }
}
